import axios from "axios";

// Network RPC URL from PRD
const RPC_URL = "https://mainnet-rpc.ogpuscan.io";
const EXPLORER_URL = "https://ogpuscan.io";
// Blockscout API endpoints
const BLOCKSCOUT_API = `${EXPLORER_URL}/api`;

// Chain details from PRD
const CHAIN_ID = 1071;
const CURRENCY_SYMBOL = "OGPU";
const CURRENCY_NAME = "OpenGPU";
const DECIMALS = 18;

// Interface for wallet information
export interface WalletInfo {
  address: string;
  balance: string;
  tokenBalance: string;
  transactions: Transaction[];
  walletAge: number;
  firstTxDate: string;
  walletScore: number;
}

export interface Transaction {
  id: number;
  hash: string;
  type: "in" | "out";
  amount: string;
  value: string;
  timestamp: string;
  from: string;
  to: string;
  status: "completed" | "pending" | "failed";
}

export interface Token {
  name: string;
  symbol: string;
  balance: string;
  value: string;
  change: number;
  contractAddress: string;
}

/**
 * Fetches wallet information from the OpenGPU Network
 * @param address The wallet address to fetch
 * @returns Promise with wallet details
 */
export const getWalletInfo = async (address: string): Promise<WalletInfo> => {
  // Create instance of axios with custom config
  const api = axios.create({
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  try {
    // Get wallet balance through RPC call
    const balanceResponse = await api.post(RPC_URL, {
      jsonrpc: "2.0",
      method: "eth_getBalance",
      params: [address, "latest"],
      id: 1,
    });

    // Get balance in ETH format (from wei)
    const balanceWei = parseInt(balanceResponse.data.result, 16) || 0;
    const balanceEth = balanceWei / 10 ** DECIMALS;

    // For the transactions, use the Blockscout API
    // Blockscout API format
    const txUrl = `${BLOCKSCOUT_API}?module=account&action=txlist&address=${address}`;
    console.log("Fetching transactions from:", txUrl);
    const txResponse = await api.get(txUrl);

    let transactions: Transaction[] = [];

    // If API response is successful
    if (
      txResponse.data &&
      txResponse.data.status === "1" &&
      Array.isArray(txResponse.data.result)
    ) {
      transactions = txResponse.data.result.map((tx: any, index: number) => {
        const isIncoming =
          tx.to && tx.to.toLowerCase() === address.toLowerCase();

        // Convert value from wei to OGPU
        const valueInWei = tx.value ? tx.value : "0";
        const valueInEth = parseInt(valueInWei) / 10 ** DECIMALS;

        return {
          id: index + 1,
          hash: tx.hash,
          type: isIncoming ? "in" : "out",
          amount: `${valueInEth.toFixed(4)} ${CURRENCY_SYMBOL}`,
          value: `$${(valueInEth * 1807.68).toFixed(2)}`, // Using current price $1,807.68
          timestamp: tx.timeStamp
            ? new Date(parseInt(tx.timeStamp) * 1000).toISOString()
            : new Date().toISOString(),
          from: tx.from,
          to: tx.to,
          status: tx.isError === "0" ? "completed" : "failed",
        };
      });
    } else {
      console.log("Transaction data format:", txResponse.data);
      throw new Error(
        "No transaction data available or invalid response format"
      );
    }

    // Calculate wallet age based on first transaction
    let walletAge = 0;
    let firstTxDate = "";

    if (transactions.length > 0) {
      const sortedTx = [...transactions].sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      const firstTx = sortedTx[0];
      const firstTxTime = new Date(firstTx.timestamp).getTime();
      const now = new Date().getTime();
      walletAge = Math.floor((now - firstTxTime) / (1000 * 60 * 60 * 24)); // Age in days
      firstTxDate = new Date(firstTx.timestamp).toLocaleDateString();
    }

    // Calculate wallet score
    const walletScore = calculateWalletScore(
      transactions,
      walletAge,
      balanceEth
    );

    return {
      address,
      balance: `$${(balanceEth * 1807.68).toFixed(2)}`, // Using current price
      tokenBalance: balanceEth.toFixed(6),
      transactions,
      walletAge,
      firstTxDate,
      walletScore,
    };
  } catch (error) {
    console.error("Error fetching wallet info:", error);
    throw new Error(
      `Failed to fetch wallet information: ${(error as Error).message}`
    );
  }
};

/**
 * Calculates a wallet score based on activity, value, and age
 */
const calculateWalletScore = (
  transactions: Transaction[],
  walletAge: number,
  balance: number
): number => {
  // Activity score (0-100) based on number of transactions
  const activityScore = Math.min(100, transactions.length * 2);

  // Value score (0-100) based on OGPU balance
  const valueScore = Math.min(100, balance / 100);

  // Age score (0-100) based on wallet age in days
  const ageScore = Math.min(100, walletAge / 3);

  // Calculate weighted average (can adjust weights based on importance)
  const weightedScore = activityScore * 0.4 + valueScore * 0.4 + ageScore * 0.2;

  return Math.round(weightedScore);
};

/**
 * Fetches token balances for a wallet
 */
export const getWalletTokens = async (address: string): Promise<Token[]> => {
  const api = axios.create({
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  try {
    // Get OGPU balance from the wallet info
    const balanceResponse = await api.post(RPC_URL, {
      jsonrpc: "2.0",
      method: "eth_getBalance",
      params: [address, "latest"],
      id: 1,
    });

    // Get balance in ETH format (from wei)
    const balanceWei = parseInt(balanceResponse.data.result, 16) || 0;
    const balanceOGPU = balanceWei / 10 ** DECIMALS;

    // Get token list from Blockscout API
    const tokenUrl = `${BLOCKSCOUT_API}?module=account&action=tokenlist&address=${address}`;
    console.log("Fetching tokens from:", tokenUrl);
    const tokenResponse = await api.get(tokenUrl);

    const tokens: Token[] = [];

    // Add native OGPU token
    tokens.push({
      name: CURRENCY_NAME,
      symbol: CURRENCY_SYMBOL,
      balance: balanceOGPU.toFixed(6),
      value: `$${(balanceOGPU * 1807.68).toFixed(2)}`, // Using current price
      change: 42.0, // Current percentage change
      contractAddress: "0x0000000000000000000000000000000000000000", // Native token has zero address
    });

    // If API response has token data
    if (
      tokenResponse.data &&
      tokenResponse.data.status === "1" &&
      Array.isArray(tokenResponse.data.result)
    ) {
      // Process each token from the response
      tokenResponse.data.result.forEach((token: any) => {
        if (token && token.balance) {
          const tokenDecimals = parseInt(token.decimals || "18", 10);
          const rawBalance = token.balance || "0";
          const tokenBalance = parseInt(rawBalance) / 10 ** tokenDecimals;

          tokens.push({
            name: token.name || "Unknown Token",
            symbol: token.symbol || "???",
            balance: tokenBalance.toFixed(
              tokenDecimals > 8 ? 8 : tokenDecimals
            ),
            value: `$${(tokenBalance * 0.1).toFixed(2)}`, // Placeholder price calculation
            change: 0, // Price change data not available
            contractAddress: token.contractAddress || "",
          });
        }
      });
    }

    return tokens;
  } catch (error) {
    console.error("Error fetching wallet tokens:", error);
    throw new Error(
      `Failed to fetch token information: ${(error as Error).message}`
    );
  }
};

/**
 * Validates if an address is a valid Ethereum address
 */
export const isValidAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};
